import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import "./AddServiceManForm.css";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";

const AddServiceManForm = ({
  open,
  onClose,
  serviceMan,
  serviceList,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    serviceManName: "",
    phone: "",
    address: "",
    location: "",
    service: [],
    experience: "",
    district: "",
    email: "",
    postalName: "",
    zipNumber: "",
    profilePic: null,
    profilePicUrl: null,
  });

  const [errors, setErrors] = useState({});

  const serviceNamesWithIds = serviceList.map((service) => ({
    id: service.id,
    name: service.service,
  }));

  useEffect(() => {
    if (serviceMan) {
      setFormData({
        serviceManName: serviceMan.worker_name,
        phone: serviceMan.mobile_number,
        address: serviceMan.address,
        location: serviceMan.location,
        service: serviceMan.service,
        experience: serviceMan.experience,
        district: serviceMan.district,
        email: serviceMan.email,
        postalName: serviceMan.postal_name,
        zipNumber: serviceMan.zipnumber,
        profilePic: null,
        profilePicUrl: serviceMan.worker_image || null,
      });
    } else {
      setFormData({
        serviceManName: "",
        phone: "",
        address: "",
        location: "",
        service: [],
        experience: "",
        district: "",
        email: "",
        postalName: "",
        zipNumber: "",
        profilePic: null,
        profilePicUrl: null,
      });
    }
    setErrors({});
  }, [serviceMan]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue =
      name === "experience" && value ? parseInt(value, 10) : value;
    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData((prevData) => ({
        ...prevData,
        profilePic: file,
        profilePicUrl: url,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, profilePic: "" }));
    }
  };

  useEffect(() => {
    return () => {
      if (formData.profilePicUrl) {
        URL.revokeObjectURL(formData.profilePicUrl);
      }
    };
  }, [formData.profilePicUrl]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.serviceManName) newErrors.serviceManName = "Name is required";
    if (!formData.phone || !/^\d{10}$/.test(formData.phone))
      newErrors.phone = "A valid 10-digit phone number is required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "A valid email is required";
    if (!formData.service.length)
      newErrors.service = "Please select at least one service";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.district) newErrors.district = "District is required";
    if (!formData.postalName) newErrors.postalName = "District is required";
    if (
      formData.experience &&
      (!Number.isInteger(Number(formData.experience)) ||
        formData.experience < 0)
    )
      newErrors.experience = "Experience must be a non-negative whole number";
    if (formData.zipNumber && !/^\d{6}$/.test(formData.zipNumber))
      newErrors.zipNumber = "A valid 6-digit zip code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const serviceDetails = { ...formData, service: formData.service };
      onSubmit(serviceDetails);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="service-dialog-title"
      >
        <DialogTitle id="service-dialog-title" style={{ color: "#ff4d4d" }}>
          {serviceMan ? "Update Service Man" : "Add New Service Man"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Please enter the service man details below:
          </Typography>
          <div className="profile-pic-container">
            <div className="profile">
              <label
                style={{
                  background: formData.profilePicUrl
                    ? `url(${formData.profilePicUrl}) center / cover`
                    : "rgb(182, 170, 170)",
                }}
              >
                {!formData.profilePicUrl && (
                  <span className="user">
                    <AccountCircleIcon style={{ fontSize: "15rem" }} />
                  </span>
                )}
                <span className="camera">
                  <AddIcon />
                </span>
                <input type="file" onChange={handleFileChange} />
              </label>
            </div>
          </div>

          <TextField
            autoFocus
            margin="dense"
            name="serviceManName"
            label="Technician Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.serviceManName}
            onChange={handleChange}
            error={!!errors.serviceManName}
            helperText={errors.serviceManName}
          />
          <FormControl fullWidth margin="dense" error={!!errors.service}>
            <InputLabel id="service-label">Service</InputLabel>
            <Select
              labelId="service-label"
              name="service"
              value={formData.service}
              multiple
              label="Select Service"
              onChange={handleChange}
              renderValue={(selected) =>
                serviceNamesWithIds
                  .filter((service) => selected.includes(service.id))
                  .map((service) => service.name)
                  .join(", ")
              }
            >
              {serviceNamesWithIds.map((serviceName) => (
                <MenuItem key={serviceName.id} value={serviceName.id}>
                  {serviceName.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            name="phone"
            label="Phone Number"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
          />

          <TextField
            margin="dense"
            name="experience"
            label="Experience (Years)"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.experience}
            onChange={handleChange}
            error={!!errors.experience}
            helperText={errors.experience}
          />
          <TextField
            margin="dense"
            name="address"
            label="Address"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.address}
            onChange={handleChange}
            error={!!errors.address}
            helperText={errors.address}
          />
          <TextField
            margin="dense"
            name="location"
            label="Location"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.location}
            onChange={handleChange}
            error={!!errors.location}
            helperText={errors.location}
          />
          <TextField
            margin="dense"
            name="district"
            label="District"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.district}
            onChange={handleChange}
            error={!!errors.district}
            helperText={errors.district}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            margin="dense"
            name="postalName"
            label="Postal Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.postalName}
            onChange={handleChange}
            error={!!errors.postalName}
            helperText={errors.postalName}
          />
          <TextField
            margin="dense"
            name="zipNumber"
            label="Zip Number"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.zipNumber}
            onChange={handleChange}
            error={!!errors.zipNumber}
            helperText={errors.zipNumber}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            color="error"
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            style={{ textTransform: "none" }}
          >
            {serviceMan ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddServiceManForm;
